import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { getPacks, getTransactions, createCheckout, getStripePublicConfig, getSettings } from '../lib/api';
import { redirectToPayment } from '../lib/paymentRedirect';
import Footer from '../components/Footer';
import InlineEditable from '../components/InlineEditable';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PromoCodeInput from '../components/PromoCodeInput';
import PayWithCryptoButton from '../components/PayWithCryptoButton';
import { toast } from 'sonner';
import { Wallet, ArrowUpRight, ArrowDownRight, Clock, Package, Sparkles } from 'lucide-react';

export default function WalletPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [buying, setBuying] = useState(null); // pack_id currently being purchased
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [packsEnabled, setPacksEnabled] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    async function load() {
      try {
        const [p, tx, sp, settingsRes] = await Promise.all([
          getPacks(),
          getTransactions(),
          getStripePublicConfig(),
          getSettings(),
        ]);
        setPacks(p.data.packs || []);
        setTransactions(tx.data.transactions || []);
        setStripeEnabled(sp.data?.enabled === true);
        const ff = settingsRes.data?.settings?.feature_flags || {};
        setPacksEnabled(ff.packs !== false);
      } catch (e) { console.error(e); }
    }
    load();
  }, [user, navigate, authLoading]);

  const handleBuyPack = async (packId) => {
    if (!stripeEnabled) {
      toast.info(t('paymentComingSoon') || 'Paiements bientôt disponibles');
      return;
    }
    setBuying(packId);
    try {
      const res = await createCheckout({
        type: 'point_pack',
        pack_id: packId,
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || undefined,
      });
      const ok = await redirectToPayment(res.data);
      if (!ok) {
        toast.error('URL de paiement manquante');
        setBuying(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur de paiement';
      toast.error(msg);
      setBuying(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-rolex-green">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[hsl(var(--gold)/0.18)]">
        <div className="absolute inset-0 hero-spotlight pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
          <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> <InlineEditable tKey="wallet">{t('wallet') || 'Wallet'}</InlineEditable></p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-cream">
            <InlineEditable tKey="yourBalanceLabel">{t('yourBalanceLabel')}</InlineEditable>
          </h1>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Premium balance card */}
        <div className="relative overflow-hidden rounded-3xl mb-10 gold-glow-strong" data-testid="wallet-balance">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0E4936] via-[#0B3D2E] to-[#08291F]" />
          <div className="absolute inset-0 hero-spotlight opacity-80 pointer-events-none" />
          <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
          <div className="relative p-8 lg:p-10 flex items-center justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-3"><InlineEditable tKey="yourBalanceLabel">{t('yourBalanceLabel')}</InlineEditable></p>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-cream/80 text-3xl lg:text-4xl font-bold">£</span>
                <span className="font-mono text-6xl lg:text-7xl font-bold text-gold-gradient tabular-nums">{user.points_balance || 0}</span>
              </div>
              <p className="text-sm text-cream/55"><InlineEditable tKey="pointEquivalent">{t('pointEquivalent')}</InlineEditable></p>
            </div>
            <div className="hidden sm:flex w-20 h-20 rounded-2xl bg-gold-gradient items-center justify-center shadow-[0_8px_30px_rgba(201,169,97,0.4)]">
              <Wallet className="w-10 h-10 text-rolex-green" strokeWidth={2} />
            </div>
          </div>
        </div>

        <Tabs defaultValue={packsEnabled ? "packs" : "history"}>
          <TabsList className="bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm border border-[hsl(var(--gold)/0.20)] mb-6 p-1.5 h-auto">
            {packsEnabled && (
              <TabsTrigger value="packs" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Package className="w-4 h-4" /> <InlineEditable tKey="pointPacks">{t('pointPacks')}</InlineEditable></TabsTrigger>
            )}
            <TabsTrigger value="history" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Clock className="w-4 h-4" /> <InlineEditable tKey="transactionHistory">{t('transactionHistory')}</InlineEditable></TabsTrigger>
          </TabsList>

          {packsEnabled && (
          <TabsContent value="packs">
            <div className="mb-5 max-w-xl">
              <PromoCodeInput
                variant="dark"
                applied={appliedPromo}
                onApplied={setAppliedPromo}
                context={{ pack_id: undefined }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {packs.map((pack) => (
                <div key={pack._id} className={`relative rounded-2xl p-6 text-center card-glass card-premium-hover ${pack.popular ? 'gold-glow-strong' : ''}`}>
                  {pack.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-gradient text-rolex-green border-none text-[10px] font-bold tracking-widest uppercase z-10 px-3 py-1"><InlineEditable tKey="mostPopular">{t('mostPopular')}</InlineEditable></Badge>}
                  <h3 className="font-display text-lg font-bold mb-2 text-cream">{pack.name}</h3>
                  {/* Credit received (with bonus included) — displayed in £ */}
                  <p className="text-[10px] text-cream/55 uppercase tracking-wider mb-1"><InlineEditable tKey="creditReceived">{t('creditReceived') || 'Crédit reçu'}</InlineEditable></p>
                  <div className="flex items-baseline justify-center gap-1 mb-3">
                    <span className="text-cream/70 text-lg font-bold">£</span>
                    <span className="font-mono text-5xl font-bold text-gold-gradient">
                      {Math.round((pack.points || 0) * (1 + (pack.bonus_pct || 0) / 100))}
                    </span>
                  </div>
                  {pack.bonus_pct > 0 && <Badge variant="outline" className="mb-4 border-green-400/50 text-green-300 bg-green-900/30 text-[10px] uppercase tracking-wider">+{pack.bonus_pct}% <InlineEditable tKey="bonus">{t('bonus')}</InlineEditable></Badge>}
                  <p className="text-[10px] text-cream/55 uppercase tracking-wider mb-1"><InlineEditable tKey="youPay">{t('youPay') || 'Vous payez'}</InlineEditable></p>
                  <p className="font-mono text-2xl font-bold text-cream mb-5">£{pack.price_gbp}</p>
                  <Button
                    className={`w-full btn-press rounded-xl tracking-wider uppercase text-xs font-bold ${pack.popular ? 'btn-gold' : 'border border-[hsl(var(--gold)/0.4)] bg-transparent text-cream hover:bg-[hsl(var(--gold)/0.10)]'}`}
                    onClick={() => handleBuyPack(pack._id)}
                    disabled={buying === pack._id}
                    data-testid={`buy-pack-${pack._id}`}
                  >
                    {buying === pack._id ? '...' : <InlineEditable tKey="buyPoints">{t('buyPoints')}</InlineEditable>}
                  </Button>
                  <PayWithCryptoButton
                    amountGbp={pack.price_gbp}
                    payload={{ type: 'point_pack', pack_id: pack._id }}
                    className="w-full mt-2 rounded-xl text-xs uppercase tracking-wider font-bold"
                  />
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-cream/45 mt-6 tracking-wide"><InlineEditable tKey="paymentInfo">{t('paymentInfo')}</InlineEditable></p>
          </TabsContent>
          )}

          <TabsContent value="history" data-testid="wallet-transactions-table">
            <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
              {transactions.length === 0 ? (
                <div className="p-10 text-center text-rolex-green/55"><Clock className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" /><p className="font-semibold"><InlineEditable tKey="noTransactions">{t('noTransactions')}</InlineEditable></p></div>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead><InlineEditable tKey="type">{t('type')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="description">{t('description')}</InlineEditable></TableHead><TableHead className="text-right"><InlineEditable tKey="amount">{t('amount')}</InlineEditable></TableHead><TableHead className="text-right"><InlineEditable tKey="date">{t('date')}</InlineEditable></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {transactions.map((tx) => (
                      <TableRow key={tx._id}>
                        <TableCell>{tx.amount > 0 ? <Badge variant="outline" className="border-green-600/40 text-green-700 gap-1 text-xs"><ArrowUpRight className="w-3 h-3" /> <InlineEditable tKey="credit">{t('credit')}</InlineEditable></Badge> : <Badge variant="outline" className="border-red-500/40 text-red-600 gap-1 text-xs"><ArrowDownRight className="w-3 h-3" /> <InlineEditable tKey="debit">{t('debit')}</InlineEditable></Badge>}</TableCell>
                        <TableCell className="text-sm">{tx.description}</TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${tx.amount > 0 ? 'text-green-700' : 'text-red-600'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</TableCell>
                        <TableCell className="text-right text-xs text-rolex-green/55">{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
